import Quadtree from 'quadtree-lib';

import Game from '../game';
import Camera from '../camera';
import BaseObject, { IdentifierType, BaseObjectParams } from '../baseObject';
import Element from '../element/element';
import Background, { BackgroundParams } from '../sprite/background';
import Cursor from '../element/cursor';
import Music, { MusicParams } from '../audio/music';
import Playlist, { PlaylistParams } from '../audio/playlist';
import DoubleMusic, { DoubleMusicParams } from '../audio/doubleMusic';
import { SpriteParams } from '../sprite/sprite';

import { Iterator } from '../../utils/types';


export interface SceneParams extends BaseObjectParams {
  width: number;
  height: number;
  backgroundSpriteParams?: Partial<SpriteParams>;
  musicsParamsArray?: Partial<MusicParams | DoubleMusicParams>[];
}

export default class Scene extends BaseObject implements SceneParams {
  width = 1280;
  height = 768;
  elements: Iterator<Element> = {};
  quadtree!: Quadtree<Quadtree.QuadtreeItem>;
  backgrounds: Array<Background> = [];
  backgroundSpriteParams?: Partial<SpriteParams>;
  musicsParamsArray?: Partial<MusicParams | DoubleMusicParams>[];

  camera!: Camera;
  zoomRatio = 1;
  cursor!: Cursor;
  music!: Music | Playlist | DoubleMusic;

  constructor(params: SceneParams) {
    super();
    this.applyParams(params);
  }

  create(): Scene {
    this.elements = {};
    this.quadtree = new Quadtree({
      width: this.width,
      height: this.height,
      maxElements: 100000,
    });

    /* Camera */
    this.camera = new Camera({
      contexts: [
        Game.instance.contexts.backgroundCanvas,
        Game.instance.contexts.mainCanvas,
        Game.instance.contexts.fogCanvas,
        Game.instance.contexts.foregroundCanvas,
      ] // every ctx except the interface
    });

    /* Background */
    if (this.backgroundSpriteParams) {
      this.backgrounds = [new Background({
        context: Game.instance.contexts.backgroundCanvas,
        x: 0,
        y: 0,
        maxX: this.width,
        maxY: this.height,
        loop: true,
        spriteParams: { ...this.backgroundSpriteParams },
      } as BackgroundParams)];
    }

    /* Music */
    if (this.musicsParamsArray) {
      this.music = new Playlist({
        loop: true,
        shuffle: true,
        musicsParamsArray: this.musicsParamsArray
          .map((musicParams) => {
            return {
              ...musicParams,
            };
          })
      } as PlaylistParams);
      this.music.play();
    }

    return this;
  }

  reset() {
    Object.values(Game.instance.contexts).forEach(c => c.reset());

    Object.values(this.elements).forEach(el => el.remove());
    this.quadtree.clear();

    this?.music?.pause();
  }

  tick(modifier: number): Scene {
    Game.instance.clearAll();

    /* BG */
    this.backgrounds.forEach(bg => bg.draw());

    /* elements */
    Object.values(this.elements)
      .forEach(element => {
        if (!Game.instance.paused)
          element.update(modifier);
        element?.draw();
      });

    return this;
  }

  getElementsByIds(ids: Array<number>): Array<Element> {
    return ids
      .reduce((acc: Element[], id) => {
        if (this.elements[id]) acc.push(this.elements[id])
        return acc;
      }, []);
  }
}
